import { createSlice } from '@reduxjs/toolkit';

import { REQUEST } from '../../constants/request';
import { INITIAL_LOADING_STATE } from '../../utils/asyncThunkRequest';

import { getCachedUserData } from '../../utils/userCache';

import { fetchMeUserExtraReducers } from './asyncThunk/fetchMeUser';
import { fetchRebuzzChannelsExtraReducers } from './asyncThunk/fetchRebuzzChannels';
import { fetchThreadChannelsExtraReducers } from './asyncThunk/fetchThreadChannels';
import { fetchUserChannelsExtraReducers } from './asyncThunk/fetchUserChannels';
import { fetchUserStreamsExtraReducers } from './asyncThunk/fetchUserStreams';
import { fetchUserOrgsExtraReducers } from './asyncThunk/fetchUserOrgs';

export { fetchMeUser } from './asyncThunk/fetchMeUser';
export { fetchRebuzzChannels } from './asyncThunk/fetchRebuzzChannels';
export { fetchThreadChannels } from './asyncThunk/fetchThreadChannels';
export { fetchUserChannels } from './asyncThunk/fetchUserChannels';
export { fetchUserStreams } from './asyncThunk/fetchUserStreams';
export { fetchUserOrgs } from './asyncThunk/fetchUserOrgs';

const INITIAL_USER_STATE = {
  user: {
    name: '',
    email: '',
    providers: [],
    id: null,
    properties: {},
    pins: {},
    background: '',
    metadata: {
      createdAt: '',
    },
    wallet: {
      coins: 0,
      currency: 0,
    },
    active: true,
  },
  userRoleMapping: {
    mapping: [],
    roles: [],
  },
  channels: {},
  streams: {},
  organizations: {},
  channelsByRoles: {},
  streamsByRoles: {},
  orgsByRoles: {},
  loading: REQUEST.idle,
  currentRequestId: undefined,
  error: null,
  currentRequestIds: {},
  currentLoadings: {},
  currentErrors: {},
  rebuzzChannels: [],
  threadChannels: [],
  userMeRequest: {
    ...INITIAL_LOADING_STATE,
  },
  userOrgsRequest: {
    ...INITIAL_LOADING_STATE,
  },
  userStreamsRequest: {
    ...INITIAL_LOADING_STATE,
  },
  userChannelsRequest: {
    ...INITIAL_LOADING_STATE,
  },
};

// Restore state from localStorage if available
const getInitialState = () => {
  const cached = getCachedUserData();
  if (cached) {
    return {
      ...INITIAL_USER_STATE,
      ...cached,
      loading: REQUEST.idle,
      currentRequestId: undefined,
      error: null,
      currentRequestIds: {},
      currentLoadings: {},
      currentErrors: {},
      userMeRequest: {
        ...INITIAL_LOADING_STATE,
      },
      userOrgsRequest: {
        ...INITIAL_LOADING_STATE,
      },
      userStreamsRequest: {
        ...INITIAL_LOADING_STATE,
      },
      userChannelsRequest: {
        ...INITIAL_LOADING_STATE,
      },
    };
  }

  return INITIAL_USER_STATE;
};

export const CurrentUserSlice = createSlice({
  name: 'currentUser',
  initialState: getInitialState(),
  reducers: {
    updateCoins: (state, action) => {
      state.user.wallet.coins = action.payload;
    },
    updateCurrency: (state, action) => {
      state.user.wallet.currency = action.payload;
    },
    setInitialCurrentUserSlice: () => INITIAL_USER_STATE,
    changePersonalDataName: (state, { payload }) => {
      state.user.name = payload.name;
    },
    changePersonalDataEmail: (state, { payload }) => {
      state.user.email = payload.email;
    },
    resetCurrentUserSlice: () => INITIAL_USER_STATE,
  },
  extraReducers: builder => {
    fetchMeUserExtraReducers(builder);
    fetchRebuzzChannelsExtraReducers(builder);
    fetchThreadChannelsExtraReducers(builder);
    fetchUserChannelsExtraReducers(builder);
    fetchUserStreamsExtraReducers(builder);
    fetchUserOrgsExtraReducers(builder);
  },
});

export const {
  updateCoins,
  updateCurrency,
  setInitialCurrentUserSlice,
  changePersonalDataName,
  changePersonalDataEmail,
  resetCurrentUserSlice,
} = CurrentUserSlice.actions;

export default CurrentUserSlice.reducer;

export const selectorCurrentUser = state => state.currentUser.user;
export const selectorCurrentUserId = state => state.currentUser.user.id;
export const selectorCurrentUserName = state => state.currentUser.user.name;
export const selectorCurrentUserProfilePicUrl = state =>
  state.currentUser.user.metadata.profilePicUrl;
export const selectorCurrentUserOrganizations = state =>
  state.currentUser.organizations;
export const selectorCurrentUserStreams = state => state.currentUser.streams;
export const selectorCurrentUserChannels = state => state.currentUser.channels;
export const selectorCurrentUserThreadChannels = state =>
  state.currentUser.threadChannels;
export const selectorCurrentUserChannelsByRoles = state =>
  state.currentUser.channelsByRoles || {};
export const selectorGetUserRebuzzChannels = state =>
  state.currentUser.rebuzzChannels;
export const selectorCurrentUserRebuzzChannelsByType = type => state =>
  state.currentUser.rebuzzChannels?.filter(channel => channel?.type === type) ||
  [];
export const selectorGetGlobalUserRoleMapping = state =>
  state.currentUser.userRoleMapping;
export const selectorGetCurrentUserLoadingStatus = (state, key) =>
  state.currentUser.currentLoadings?.[key] || REQUEST.idle;
export const selectorGetUserWallet = state => state.currentUser.user.wallet;
